import React from 'react';
import { motion } from 'framer-motion';
import { Check, Heart, Package } from 'lucide-react';
import { PageTransition } from '../components/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Link } from 'react-router-dom';
export function OutreachPage() {
  return <PageTransition data-id="element-569">
      <div className="pt-24 pb-16 min-h-screen" data-id="element-570">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 text-center" data-id="element-571">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-572">
            <div className="mb-8" data-id="element-573">
              <img src="https://cdn.magicpatterns.com/uploads/mmGRaZje5gHPYZ1hRf9zTx/operationolivebranch.jpg" alt="Operation Olive Branch" className="w-64 h-auto mx-auto" data-id="element-574" />
            </div>
            <span className="text-gold-500 font-medium tracking-wider uppercase mb-2 block" data-id="element-575">
              Serving the Least of These
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-576">
              Operation Olive Branch
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed" data-id="element-577">
              3 Doves Podcast believes faith without action is incomplete. Our
              Essential Bags program provides homeless individuals with dignity
              and basic necessities — because every person is made in the image
              of God.
            </p>
          </motion.div>
        </div>

        {/* Bags Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-578">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-579">
            {/* Men's Bag */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-580">
              <Card className="h-full bg-dark-700 border-dark-600 p-8 relative overflow-hidden" data-id="element-581">
                <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-582">
                  <Package size={120} data-id="element-583" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-584">
                  Men's Essential Bag
                </h3>
                <p className="text-gold-500 mb-6" data-id="element-585">2-Gallon Care Package</p>

                <ul className="space-y-3 mb-8" data-id="element-586">
                  {['Toothpaste & Toothbrush', 'Antibacterial Wipes', 'Body Wipes', 'Dude Wipes', 'Hairbrush', 'Toiletries Kit', 'Water Bottle', 'Snack & Energy Bars'].map((item, i) => <li key={i} className="flex items-center text-white/80" data-id="element-587">
                      <Check className="w-5 h-5 text-gold-500 mr-3 flex-shrink-0" data-id="element-588" />
                      {item}
                    </li>)}
                </ul>
              </Card>
            </motion.div>

            {/* Women's Bag */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-589">
              <Card className="h-full bg-dark-700 border-dark-600 p-8 relative overflow-hidden" data-id="element-590">
                <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-591">
                  <Heart size={120} data-id="element-592" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-593">
                  Women's Essential Bag
                </h3>
                <p className="text-gold-500 mb-6" data-id="element-594">2-Gallon Care Package</p>

                <ul className="space-y-3 mb-8" data-id="element-595">
                  {['Toothpaste & Toothbrush', 'Antibacterial Wipes', 'Body Wipes', 'Feminine Hygiene Products', 'Hairbrush', 'Toiletries Kit', 'Water Bottle', 'Snack & Energy Bars'].map((item, i) => <li key={i} className="flex items-center text-white/80" data-id="element-596">
                      <Check className="w-5 h-5 text-gold-500 mr-3 flex-shrink-0" data-id="element-597" />
                      {item}
                    </li>)}
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Biblical Foundation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-598">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-599">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-600">
              Biblical Foundation
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto" data-id="element-601">
              Our outreach is rooted in Scripture and God's heart for the poor,
              broken, and marginalized.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-602">
            {[{
            title: 'Care for the Poor',
            verse: 'Proverbs 19:17 (NLT)',
            text: '"If you help the poor, you are lending to the Lord—and he will repay you!"'
          }, {
            title: 'God Sees and Provides',
            verse: 'Psalm 146:7 (NLT)',
            text: '"He gives justice to the oppressed and food to the hungry. The Lord frees the prisoners."'
          }, {
            title: 'Shelter and Compassion',
            verse: 'Isaiah 58:7 (NLT)',
            text: '"Share your food with the hungry, and give shelter to the homeless. Give clothes to those who need them…"'
          }, {
            title: 'Jesus Identifies with the Needy',
            verse: 'Matthew 25:35 (NLT)',
            text: '"For I was hungry, and you fed me. I was thirsty, and you gave me a drink. I was a stranger, and you invited me into your home."'
          }, {
            title: 'God is Close to the Broken',
            verse: 'Psalms 34:18 (NLT)',
            text: '"The Lord is close to the brokenhearted; he rescues those whose spirits are crushed."'
          }, {
            title: 'Encouragement for Dignity and Strength',
            verse: 'Philippians 4:13 (NLT)',
            text: '"For I can do everything through Christ, who gives me strength."'
          }].map((scripture, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-603">
                <Card className="h-full bg-dark-700 border-dark-600 p-6" data-id="element-604">
                  <h3 className="text-lg font-serif font-bold text-gold-400 mb-2" data-id="element-605">
                    {scripture.title}
                  </h3>
                  <p className="text-xs text-white/50 mb-3 uppercase tracking-wider" data-id="element-606">
                    {scripture.verse}
                  </p>
                  <p className="text-white/80 italic leading-relaxed" data-id="element-607">
                    {scripture.text}
                  </p>
                </Card>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-dark-800 rounded-2xl p-8 border border-dark-600" data-id="element-608">
            <h3 className="text-2xl font-serif font-bold text-white mb-6 text-center" data-id="element-609">
              Key Takeaway
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-610">
              {['God sees them', 'God cares for them', 'God calls others to help them', 'Their situation does not define their worth'].map((point, index) => <div key={index} className="flex items-start" data-id="element-611">
                  <Check className="w-5 h-5 text-gold-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-612" />
                  <p className="text-white/90 font-medium" data-id="element-613">{point}</p>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* How to Help */}
        <div className="bg-dark-800 py-16 border-y border-dark-600" data-id="element-614">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-615">
            <h2 className="text-3xl font-serif font-bold text-white mb-8" data-id="element-616">
              How You Can Help
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-617">
              <div className="p-6" data-id="element-618">
                <div className="w-12 h-12 bg-gold-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-gold-500 font-bold text-xl" data-id="element-619">
                  1
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-620">
                  Give Funds
                </h3>
                <p className="text-white/60 text-sm" data-id="element-621">
                  Your financial support purchases supplies in bulk for maximum
                  impact.
                </p>
              </div>
              <div className="p-6" data-id="element-622">
                <div className="w-12 h-12 bg-gold-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-gold-500 font-bold text-xl" data-id="element-623">
                  2
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-624">Volunteer</h3>
                <p className="text-white/60 text-sm" data-id="element-625">
                  Join us for packing days to assemble bags for distribution.
                </p>
              </div>
              <div className="p-6" data-id="element-626">
                <div className="w-12 h-12 bg-gold-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-gold-500 font-bold text-xl" data-id="element-627">
                  3
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-628">
                  Spread the Word
                </h3>
                <p className="text-white/60 text-sm" data-id="element-629">
                  Share our mission with your church or community group.
                </p>
              </div>
            </div>

            <Link to="/donate" data-id="element-630">
              <Button size="lg" className="shadow-xl shadow-gold-500/20" data-id="element-631">
                Give to Operation Olive Branch
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </PageTransition>;
}